// Halaman PUBLIK — dibuka pelanggan dari tautan WhatsApp, TANPA login.
// Sengaja tidak memakai Layout/komponen ber-auth: pelanggan bukan user
// aplikasi, dan satu import komponen ber-auth saja bisa memicu redirect ke
// /login. Satu-satunya yang dibaca dari AuthContext adalah `branding` (nama
// toko, keputusan owner 24 Sep 2026 white-label): AuthProvider memang sudah
// membungkus rute ini dan memuatnya dari endpoint PUBLIK /config/branding —
// membaca nilainya tidak memicu panggilan ber-auth apa pun.
//
// Fase 5.2: pertanyaan TIDAK lagi hardcoded (dulu 1 bintang + 1 komentar
// tertulis langsung di JSX ini). Sekarang dirender dinamis dari daftar
// `info.pertanyaan` yang dikirim backend (routers/csat.py::csat_public_info),
// supaya owner bisa menambah/mengubah pertanyaan dari halaman Pengaturan CSAT
// tanpa perlu deploy ulang frontend. Validasi "wajib" di sini hanya
// KENYAMANAN (mengunci tombol kirim) — validasi yang MENENTUKAN tetap di
// server (csat.py::_validasi_dan_susun_jawaban), karena endpoint submit ini
// publik tanpa auth dan bisa dipanggil langsung tanpa lewat UI ini.
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, CheckCircle2, Clock, ShieldCheck, Frown, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Public survey requests never attach an employee token or trigger session logout.
const publicApi = axios.create({ baseURL: API });

const LABEL_RATING = {
  1: "Sangat kecewa",
  2: "Kecewa",
  3: "Biasa saja",
  4: "Puas",
  5: "Sangat puas!",
};

function isTerisi(v) {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

function PertanyaanBintang({ pertanyaan, nilai, hover, onHover, onChange }) {
  const tampil = hover || nilai || 0;
  return (
    <div>
      <p className="text-sm font-medium text-slate-800 mb-2" data-testid={`csat-label-${pertanyaan.id}`}>
        {pertanyaan.label} {pertanyaan.wajib && <span className="text-rose-500" aria-hidden>*</span>}
      </p>
      <div className="flex justify-center gap-1.5 mb-1" onMouseLeave={() => onHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`csat-star-${pertanyaan.id}-${n}`}
            aria-label={`Beri ${n} bintang untuk ${pertanyaan.label}`}
            onClick={() => onChange(n)}
            onMouseEnter={() => onHover(n)}
            className="w-11 h-11 grid place-items-center rounded-full transition-transform active:scale-90"
          >
            <Star
              className={`w-8 h-8 transition-colors ${
                n <= tampil ? "text-amber-400 fill-amber-400" : "text-slate-300"
              }`}
            />
          </button>
        ))}
      </div>
      <p className="text-center text-xs font-medium text-slate-500 h-4" data-testid={`csat-rating-label-${pertanyaan.id}`}>
        {tampil ? LABEL_RATING[tampil] : ""}
      </p>
    </div>
  );
}

function PertanyaanPilihan({ pertanyaan, nilai, onChange }) {
  return (
    <div>
      <p className="text-sm font-medium text-slate-800 mb-2" data-testid={`csat-label-${pertanyaan.id}`}>
        {pertanyaan.label} {pertanyaan.wajib && <span className="text-rose-500" aria-hidden>*</span>}
      </p>
      <div className="flex flex-wrap gap-2">
        {(pertanyaan.opsi || []).map((o, i) => (
          <button
            key={i}
            type="button"
            data-testid={`csat-opsi-${pertanyaan.id}-${i}`}
            onClick={() => onChange(o)}
            className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
              nilai === o
                ? "bg-emerald-600 border-emerald-600 text-white"
                : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function PertanyaanTeks({ pertanyaan, nilai, onChange }) {
  return (
    <div>
      <p className="text-sm font-medium text-slate-800 mb-2" data-testid={`csat-label-${pertanyaan.id}`}>
        {pertanyaan.label} {pertanyaan.wajib && <span className="text-rose-500" aria-hidden>*</span>}
      </p>
      <textarea
        value={nilai || ""}
        onChange={(e) => onChange(e.target.value)}
        maxLength={1000}
        rows={3}
        placeholder={pertanyaan.wajib ? "Wajib diisi..." : "Opsional..."}
        data-testid={`csat-teks-${pertanyaan.id}`}
        className="w-full rounded-xl border border-slate-200 p-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
      />
    </div>
  );
}

function PertanyaanField(props) {
  const { pertanyaan } = props;
  if (pertanyaan.tipe === "bintang") return <PertanyaanBintang {...props} />;
  if (pertanyaan.tipe === "pilihan") return <PertanyaanPilihan {...props} />;
  return <PertanyaanTeks {...props} />;
}

export default function CsatSurvey() {
  const { token } = useParams();
  const { branding } = useAuth();
  const namaToko = branding?.nama_toko_sendiri || "EppleHub";
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [jawaban, setJawaban] = useState({}); // { [pertanyaan_id]: nilai }
  const [hoverBintang, setHoverBintang] = useState({}); // { [pertanyaan_id]: n }
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setLoadError(""); setInfo(null); setDone(false);
    setJawaban({}); setHoverBintang({}); setSubmitError("");
    publicApi.get(`/csat/public/${token}`)
      .then((r) => { if (alive) setInfo(r.data); })
      .catch((err) => {
        if (!alive) return;
        setLoadError(err.response?.data?.detail || "Tautan survei tidak dapat dibuka. Coba lagi beberapa saat.");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  // useMemo (bukan `const pertanyaan = info?.pertanyaan || []` biasa): array
  // literal baru di tiap render membuat dependency `pertanyaan` di bawah
  // "berubah" terus meski isinya sama, memicu warning react-hooks/exhaustive-deps.
  const pertanyaan = useMemo(() => info?.pertanyaan || [], [info]);

  const semuaWajibTerisi = useMemo(
    () => pertanyaan.every((p) => !p.wajib || isTerisi(jawaban[p.id])),
    [pertanyaan, jawaban],
  );

  const submit = async () => {
    if (!semuaWajibTerisi || submitting) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const daftarJawaban = pertanyaan
        .filter((p) => isTerisi(jawaban[p.id]))
        .map((p) => ({ pertanyaan_id: p.id, nilai: jawaban[p.id] }));
      await publicApi.post(`/csat/public/${token}/submit`, { jawaban: daftarJawaban });
      setDone(true);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || "Gagal mengirim penilaian. Periksa koneksi lalu coba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  const inisial = (info?.nama_penjual || "?").trim().charAt(0).toUpperCase() || "?";

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden" data-testid="csat-survey-card">
          {/* Header brand */}
          <div className="bg-blue-600 px-6 py-5">
            <p className="text-blue-100 text-xs font-semibold uppercase tracking-wider">{namaToko}</p>
            <p className="text-white font-semibold text-sm mt-0.5">Penilaian Pelayanan</p>
          </div>

          {loading && (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="csat-loading">Memuat survei...</div>
          )}

          {!loading && loadError && (
            <div className="p-10 text-center" data-testid="csat-load-error">
              <Frown className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="font-semibold text-slate-800 mb-1">Tautan tidak ditemukan</p>
              <p className="text-sm text-slate-500">{loadError}</p>
            </div>
          )}

          {!loading && !loadError && info?.kedaluwarsa && (
            <div className="p-10 text-center" data-testid="csat-expired">
              <Clock className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <p className="font-semibold text-slate-800 mb-1">Tautan sudah kedaluwarsa</p>
              <p className="text-sm text-slate-500">
                Maaf, masa berlaku survei ini sudah habis. Terima kasih sudah berbelanja di {info.branch || "toko kami"}.
              </p>
            </div>
          )}

          {!loading && !loadError && info && !info.kedaluwarsa && (info.sudah_diisi || done) && (
            <div className="p-6 sm:p-10 text-center" data-testid="csat-thanks" role="status">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="font-semibold text-slate-900 text-lg mb-1">Terima kasih!</p>
              <p className="text-sm text-slate-500">
                {done
                  ? "Penilaian Anda sudah kami terima dan sangat membantu kami melayani lebih baik."
                  : "Penilaian untuk transaksi ini sudah pernah dikirim sebelumnya."}
              </p>
              {info.google_review_url && <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-xs text-blue-600 font-semibold mb-2">CSAT selesai · Langkah 2 opsional</p>
                <h1 className="font-semibold text-slate-900 text-lg">Bagikan pengalaman di Google Maps</h1>
                <p className="text-sm text-slate-500 mt-2 mb-5">Jika berkenan, tulis ulasan sesuai pengalaman Anda. Ulasan ini bersifat publik dan sukarela.</p>
                <a href={info.google_review_url} target="_blank" rel="noopener noreferrer" data-testid="csat-google-review"
                  className="w-full min-h-12 inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600">
                  Lanjut ke Google Maps <ArrowUpRight className="w-4 h-4 shrink-0" />
                </a>
                <p className="text-xs text-slate-400 mt-3">Dibuka di tab baru. Anda juga boleh selesai di sini.</p>
              </div>}
            </div>
          )}

          {!loading && !loadError && info && !info.kedaluwarsa && !info.sudah_diisi && !done && (
            <div className="p-6">
              {info.google_review_url && <p className="text-xs text-blue-600 font-semibold mb-5">1. CSAT Operational → 2. Google Maps (opsional)</p>}
              {/* Frontliner yang dinilai — gaya kartu ulasan Google */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center font-bold text-lg shrink-0">
                  {inisial}
                </div>
                <div>
                  <p className="font-semibold text-slate-900 leading-tight">{info.nama_penjual || "Tim kami"}</p>
                  <p className="text-xs text-slate-500">{info.branch || namaToko}</p>
                </div>
              </div>

              <h1 className="font-semibold text-slate-900 text-lg leading-snug mb-2">
                Bantu kami menilai pengalaman Anda
              </h1>

              {/* Keterangan privasi — teks WAJIB persis dari spec owner */}
              <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-5">
                <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-xs text-emerald-800" data-testid="csat-privacy-note">
                  Jawaban Anda hanya bisa dilihat oleh Manager dan Owner, Sales tidak akan mengetahuinya
                </p>
              </div>

              {/* Pertanyaan dinamis — dikonfigurasi Manager/Owner lewat halaman
                  Pengaturan CSAT (lihat CsatPengaturan.jsx / routers/csat.py). */}
              <div className="space-y-5 mb-5" data-testid="csat-pertanyaan-list">
                {pertanyaan.map((p) => (
                  <PertanyaanField
                    key={p.id}
                    pertanyaan={p}
                    nilai={jawaban[p.id]}
                    hover={hoverBintang[p.id] || 0}
                    onHover={(n) => setHoverBintang((s) => ({ ...s, [p.id]: n }))}
                    onChange={(v) => setJawaban((s) => ({ ...s, [p.id]: v }))}
                  />
                ))}
              </div>

              {submitError && (
                <p className="text-sm text-rose-600 mb-3" data-testid="csat-submit-error">{submitError}</p>
              )}

              <button
                type="button"
                onClick={submit}
                disabled={!semuaWajibTerisi || submitting}
                data-testid="csat-submit"
                className="w-full h-12 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 transition-colors"
              >
                {submitting ? "Mengirim..." : "Kirim Penilaian"}
              </button>
            </div>
          )}
        </div>
        <p className="text-center text-xs text-slate-400 mt-4">
          Survei kepuasan pelanggan · {namaToko}
        </p>
      </div>
    </div>
  );
}
